import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
    downloadFileByUrl,
    loadCassetteBatchesMetadata,
    loadGuaranteeCassettes,
    submitGuaranteeSend,
} from './api';
import {
    cassetteFallbackGuarantee,
    cassetteFallbackMetadata,
} from './cassetteFallbacks';


function buildBatchKey(batch) {
    return `${batch.territory.id}:${batch.arrival_date}:${batch.number_kits}`;
}


function formatDate(value) {
    if (!value) {
        return '—';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleDateString('ru-RU');
}


function normalizeText(value) {
    return String(value || '').trim().toLocaleLowerCase('ru-RU');
}


function extractErrorMessage(error, fallback) {
    return error?.response?.data?.detail || fallback;
}


function CreatedRequestCard({ requestPayload, onDownloadExcel, onDownloadPdf }) {
    if (!requestPayload) {
        return null;
    }

    return (
        <div className="shell-requestActionCard">
            <span className="shell-cardLabel">Созданная гарантийная заявка</span>
            <div className="shell-requestDetail shell-requestDetail--compact">
                <div className="shell-requestDetailGrid">
                    <div className="shell-requestDetailItem">
                        <span>Заявка</span>
                        <strong>{requestPayload.application_name}</strong>
                    </div>
                    <div className="shell-requestDetailItem">
                        <span>Slug</span>
                        <strong>{requestPayload.slug}</strong>
                    </div>
                    <div className="shell-requestDetailItem">
                        <span>Кому</span>
                        <strong>{requestPayload.whom || '—'}</strong>
                    </div>
                    <div className="shell-requestDetailItem">
                        <span>Адрес</span>
                        <strong>{requestPayload.address || '—'}</strong>
                    </div>
                </div>
            </div>
            <div className="shell-requestActionButtons">
                {requestPayload.excel_file_url ? (
                    <button
                        type="button"
                        className="shell-actionButton shell-actionButton--secondary shell-actionButton--small"
                        onClick={onDownloadExcel}
                    >
                        Скачать акт
                    </button>
                ) : null}
                {requestPayload.pdf_file_url ? (
                    <button
                        type="button"
                        className="shell-actionButton shell-actionButton--secondary shell-actionButton--small"
                        onClick={onDownloadPdf}
                    >
                        Скачать PDF
                    </button>
                ) : null}
            </div>
        </div>
    );
}


const initialGuaranteeForm = {
    whom: '',
    address: '',
    logisticsFile: null,
};


export default function CassettesPage() {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('batches');
    const [reloadKey, setReloadKey] = useState(0);
    const [metadataState, setMetadataState] = useState({
        data: cassetteFallbackMetadata,
        loading: true,
        warning: '',
    });
    const [guaranteeState, setGuaranteeState] = useState({
        data: cassetteFallbackGuarantee,
        loading: false,
        warning: '',
    });
    const [batchSearch, setBatchSearch] = useState('');
    const [guaranteeSearch, setGuaranteeSearch] = useState('');
    const [guaranteePage, setGuaranteePage] = useState(1);
    const [guaranteeForm, setGuaranteeForm] = useState(initialGuaranteeForm);
    const [guaranteeFileKey, setGuaranteeFileKey] = useState(0);
    const [guaranteeSubmitState, setGuaranteeSubmitState] = useState({
        loading: false,
        error: '',
        success: '',
        request: null,
    });
    const [toast, setToast] = useState(null);

    useEffect(() => {
        let cancelled = false;

        async function bootstrap() {
            setMetadataState((current) => ({
                ...current,
                loading: true,
            }));

            try {
                const payload = await loadCassetteBatchesMetadata();
                if (!cancelled) {
                    setMetadataState({
                        data: payload,
                        loading: false,
                        warning: '',
                    });
                }
            } catch (_error) {
                if (!cancelled) {
                    setMetadataState({
                        data: cassetteFallbackMetadata,
                        loading: false,
                        warning: 'Не удалось загрузить партии кассет. Показан локальный пример.',
                    });
                }
            }
        }

        bootstrap();

        return () => {
            cancelled = true;
        };
    }, [reloadKey]);

    useEffect(() => {
        if (!toast) {
            return undefined;
        }

        const timer = window.setTimeout(() => {
            setToast(null);
        }, 2600);

        return () => window.clearTimeout(timer);
    }, [toast]);

    useEffect(() => {
        let cancelled = false;

        async function loadGuarantee() {
            if (activeTab !== 'guarantee') {
                return;
            }

            setGuaranteeState((current) => ({
                ...current,
                loading: true,
            }));

            try {
                const payload = await loadGuaranteeCassettes({
                    search: guaranteeSearch,
                    page: guaranteePage,
                    page_size: 50,
                });
                if (!cancelled) {
                    setGuaranteeState({
                        data: payload,
                        loading: false,
                        warning: '',
                    });
                }
            } catch (_error) {
                if (!cancelled) {
                    setGuaranteeState({
                        data: cassetteFallbackGuarantee,
                        loading: false,
                        warning: 'Не удалось загрузить гарантийные кассеты. Показан локальный пример.',
                    });
                }
            }
        }

        loadGuarantee();

        return () => {
            cancelled = true;
        };
    }, [activeTab, guaranteePage, guaranteeSearch, reloadKey]);

    const filteredBatches = useMemo(() => {
        const query = normalizeText(batchSearch);
        if (!query) {
            return metadataState.data.batches || [];
        }

        return (metadataState.data.batches || []).filter((batch) => {
            const haystack = [
                batch.territory?.name,
                batch.territory?.address,
                batch.arrival_date,
                String(batch.number_kits || ''),
            ].join(' ');
            return normalizeText(haystack).includes(query);
        });
    }, [batchSearch, metadataState.data.batches]);

    const summaryCards = [
        { label: 'Локация', value: metadataState.data.location?.city || '—' },
        { label: 'Партии', value: metadataState.loading ? '...' : (metadataState.data.summary?.batch_count ?? 0) },
        { label: 'Кассет в партиях', value: metadataState.loading ? '...' : (metadataState.data.summary?.cassette_count ?? 0) },
        { label: 'Гарантия / Подменные', value: `${metadataState.data.summary?.guarantee_count ?? 0} / ${metadataState.data.summary?.replacement_count ?? 0}` },
    ];

    async function handleRequestFileDownload(url, fallbackFilename) {
        try {
            await downloadFileByUrl(url, fallbackFilename);
        } catch (error) {
            const message = extractErrorMessage(error, 'Не удалось скачать файл.');
            setToast({ type: 'error', text: message });
        }
    }

    async function handleGuaranteeSend(event) {
        event.preventDefault();
        setGuaranteeSubmitState({
            loading: true,
            error: '',
            success: '',
            request: null,
        });

        const formData = new FormData();
        formData.append('whom', guaranteeForm.whom);
        formData.append('address', guaranteeForm.address);
        if (guaranteeForm.logisticsFile) {
            formData.append('logistics_file', guaranteeForm.logisticsFile);
        }

        try {
            const response = await submitGuaranteeSend(formData);
            setGuaranteeSubmitState({
                loading: false,
                error: '',
                success: response.detail,
                request: response.request || null,
            });
            setGuaranteeForm(initialGuaranteeForm);
            setGuaranteeFileKey((current) => current + 1);
            setToast({ type: 'success', text: response.detail });
            setReloadKey((current) => current + 1);
        } catch (error) {
            const message = extractErrorMessage(error, 'Не удалось передать гарантийные кассеты логисту.');
            setGuaranteeSubmitState({
                loading: false,
                error: message,
                success: '',
                request: null,
            });
            setToast({ type: 'error', text: message });
        }
    }

    return (
        <div className="shell-page">
            {toast ? (
                <div className="shell-toastStack">
                    <div className={`shell-toast ${toast.type === 'error' ? 'shell-toast--error' : ''}`}>
                        {toast.text}
                    </div>
                </div>
            ) : null}

            <section className="shell-section shell-section--pageTop">
                <div className="shell-pageHeaderCompact">
                    <div className="shell-pageBandIntro">
                        <h3>Кассеты</h3>
                    </div>

                    <div className="shell-pageSummary shell-pageSummary--queueTop shell-pageSummary--header">
                        {summaryCards.map((card) => (
                            <div key={card.label} className="shell-pageSummaryCard shell-pageSummaryCard--compact">
                                <span>{card.label}</span>
                                <strong>{card.value}</strong>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {metadataState.warning ? <div className="shell-banner">{metadataState.warning}</div> : null}

            <section className="shell-section">
                <div className="shell-tabRow">
                    <button
                        type="button"
                        className={`shell-tabButton${activeTab === 'batches' ? ' is-active' : ''}`}
                        onClick={() => setActiveTab('batches')}
                    >
                        Партии кассет
                    </button>
                    <button
                        type="button"
                        className={`shell-tabButton${activeTab === 'guarantee' ? ' is-active' : ''}`}
                        onClick={() => setActiveTab('guarantee')}
                    >
                        Гарантия
                    </button>
                </div>
            </section>

            {activeTab === 'batches' ? (
                <section className="shell-section">
                    <div className="shell-sectionHeader">
                        <div>
                            <h3>Активные партии кассет</h3>
                        </div>
                        <span>{metadataState.loading ? 'Загрузка...' : `${filteredBatches.length} шт.`}</span>
                    </div>

                    <div className="shell-toolbarRow">
                        <label className="shell-field shell-field--toolbar">
                            <span>Поиск по партиям</span>
                            <input
                                value={batchSearch}
                                onChange={(event) => setBatchSearch(event.target.value)}
                                placeholder="Территория, адрес, КИЦ или дата"
                            />
                        </label>
                    </div>

                    {filteredBatches.length ? (
                        <div className="shell-tableWrap">
                            <table className="shell-table">
                                <thead>
                                    <tr>
                                        <th>Территория</th>
                                        <th>КИЦ</th>
                                        <th>Дата поступления</th>
                                        <th>Всего</th>
                                        <th>GOOD</th>
                                        <th>BAD</th>
                                        <th>Дней до SLA</th>
                                        <th>Ремонт</th>
                                        <th>Действия</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredBatches.map((batch) => (
                                        <tr key={buildBatchKey(batch)} className="shell-tableRow">
                                            <td>
                                                <strong>{batch.territory.name}</strong>
                                                <div className="shell-tableHint">{batch.territory.address}</div>
                                            </td>
                                            <td>{batch.number_kits}</td>
                                            <td>{formatDate(batch.arrival_date)}</td>
                                            <td>{batch.total_count}</td>
                                            <td>{batch.good_count}</td>
                                            <td>{batch.bad_count}</td>
                                            <td>{batch.days_left}</td>
                                            <td>{batch.has_pending_repair ? 'Есть ожидание' : 'Готово'}</td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="shell-actionButton shell-actionButton--primary shell-actionButton--small"
                                                    onClick={() => navigate(`/app/cassettes/batches/${batch.territory.id}/${batch.arrival_date}/${batch.number_kits}`)}
                                                >
                                                    Детали
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="shell-emptyState shell-emptyState--compact">Партии кассет не найдены.</div>
                    )}
                </section>
            ) : (
                <>
                    <section className="shell-section">
                        <div className="shell-sectionHeader">
                            <div>
                                <h3>Гарантийные кассеты</h3>
                            </div>
                            <span>{guaranteeState.loading ? 'Загрузка...' : `${guaranteeState.data.summary?.total ?? 0} шт.`}</span>
                        </div>

                        {guaranteeState.warning ? <div className="shell-banner">{guaranteeState.warning}</div> : null}

                        <div className="shell-toolbarRow">
                            <label className="shell-field shell-field--toolbar">
                                <span>Поиск по гарантии</span>
                                <input
                                    value={guaranteeSearch}
                                    onChange={(event) => {
                                        setGuaranteeSearch(event.target.value);
                                        setGuaranteePage(1);
                                    }}
                                    placeholder="Серийный номер, инженер, комментарий"
                                />
                            </label>
                        </div>

                        {(guaranteeState.data.results?.items || []).length ? (
                            <div className="shell-tableWrap">
                                <table className="shell-table">
                                    <thead>
                                        <tr>
                                            <th>Серийный номер</th>
                                            <th>Партия / система</th>
                                            <th>Статус ремонта</th>
                                            <th>Статус движения</th>
                                            <th>Инженер</th>
                                            <th>Дата ремонта</th>
                                            <th>Действия</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {guaranteeState.data.results.items.map((module) => (
                                            <tr key={module.id} className="shell-tableRow">
                                                <td><strong>{module.serial_number}</strong></td>
                                                <td>
                                                    {module.party_number || '—'}
                                                    <div className="shell-tableHint">{module.system || '—'}</div>
                                                </td>
                                                <td>{module.repair_status || '—'}</td>
                                                <td>{module.movement_status || '—'}</td>
                                                <td>{module.engineer || '—'}</td>
                                                <td>{formatDate(module.date_repair)}</td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="shell-actionButton shell-actionButton--primary shell-actionButton--small"
                                                        onClick={() => navigate(`/app/cassettes/guarantee/${module.serial_number}`)}
                                                    >
                                                        Детали
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="shell-emptyState shell-emptyState--compact">Гарантийных кассет сейчас нет.</div>
                        )}
                    </section>

                    <section className="shell-section">
                        <div className="shell-sectionHeader">
                            <div>
                                <h3>Передача гарантии логисту</h3>
                            </div>
                        </div>

                        <form className="shell-searchForm shell-searchForm--compact" onSubmit={handleGuaranteeSend}>
                            <label className="shell-field">
                                <span>Кому</span>
                                <input
                                    type="text"
                                    value={guaranteeForm.whom}
                                    onChange={(event) => setGuaranteeForm((current) => ({
                                        ...current,
                                        whom: event.target.value,
                                    }))}
                                    placeholder="Например, МСК"
                                />
                            </label>

                            <label className="shell-field">
                                <span>Адрес</span>
                                <input
                                    type="text"
                                    value={guaranteeForm.address}
                                    onChange={(event) => setGuaranteeForm((current) => ({
                                        ...current,
                                        address: event.target.value,
                                    }))}
                                    placeholder="Адрес отправки"
                                />
                            </label>

                            <label className="shell-field">
                                <span>Акт приёма-передачи</span>
                                <input
                                    key={guaranteeFileKey}
                                    type="file"
                                    accept=".xls,.xlsx"
                                    onChange={(event) => setGuaranteeForm((current) => ({
                                        ...current,
                                        logisticsFile: event.target.files?.[0] || null,
                                    }))}
                                />
                            </label>

                            <div className="shell-searchActions">
                                <button
                                    type="submit"
                                    className="shell-actionButton shell-actionButton--primary"
                                    disabled={guaranteeSubmitState.loading}
                                >
                                    {guaranteeSubmitState.loading ? 'Передаем...' : 'Передать логисту'}
                                </button>
                            </div>
                        </form>

                        {guaranteeSubmitState.error ? (
                            <div className="shell-inlineMessage shell-inlineMessage--error">{guaranteeSubmitState.error}</div>
                        ) : null}
                        {guaranteeSubmitState.success ? (
                            <div className="shell-inlineMessage">{guaranteeSubmitState.success}</div>
                        ) : null}
                        {guaranteeSubmitState.request ? (
                            <CreatedRequestCard
                                requestPayload={guaranteeSubmitState.request}
                                onDownloadExcel={() => handleRequestFileDownload(guaranteeSubmitState.request.excel_file_url, `${guaranteeSubmitState.request.slug}-act.xlsx`)}
                                onDownloadPdf={() => handleRequestFileDownload(guaranteeSubmitState.request.pdf_file_url, `${guaranteeSubmitState.request.slug}.pdf`)}
                            />
                        ) : null}
                    </section>
                </>
            )}
        </div>
    );
}
